import { useState, useEffect, useRef } from "react";
import { StartMenu } from "./StartMenu";
import { useWindowContext } from "./WindowContext";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { testIds } from "../../lib/testIds";
import { NipeTrayIcon } from "../nipe/NipeTrayIcon";
import { NipePendingWritesTray } from "../nipe/NipePendingWritesTray";
import { getXpIcon, XP_ICON_FALLBACK } from "../../lib/desktop/iconMap";

export const BAR_HEIGHT = 58;
const BTN_H = BAR_HEIGHT - 12;

function useTime() {
  const [time, setTime] = useState<Date | null>(null);
  useEffect(() => {
    setTime(new Date());
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

export function Taskbar() {
  const [startOpen, setStartOpen] = useState(false);
  const startBtnRef = useRef<HTMLButtonElement>(null);
  const time = useTime();
  const { windows, openWindow, focusWindow } = useWindowContext();
  const isMobile = useMediaQuery("(max-width: 767px)");

  const visibleWindows = windows.filter((w) => w.state !== "closed");
  const topWindow = windows
    .filter((w) => w.state === "open")
    .sort((a, b) => b.zIndex - a.zIndex)[0];

  function handleItemClick(path: string) {
    const win = windows.find((w) => w.path === path);
    if (win) {
      if (win.state === "closed" || win.state === "minimized") {
        openWindow(path);
      } else {
        focusWindow(path);
      }
    }
  }

  // XP taskbar gradient — monochrome dark. Sits on top of the desktop with a
  // 1px lighter top edge so it pops against the wallpaper.
  const taskbarBg = "linear-gradient(180deg, var(--xp-taskbar-from) 0%, var(--xp-taskbar-to) 100%)";
  const startBg = "linear-gradient(180deg, var(--xp-start-from) 0%, var(--xp-start-to) 100%)";

  return (
    <div
      data-testid={testIds.taskbar.root}
      className="fixed bottom-0 left-0 right-0 flex items-center px-1.5 z-[9000]"
      style={{
        height: BAR_HEIGHT,
        background: taskbarBg,
        borderTop: "1px solid rgba(255,255,255,0.4)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.3), 0 -1px 0 rgba(0,0,0,0.4)",
        gap: 4,
        boxSizing: "border-box",
      }}
    >
      <div className="relative flex-shrink-0">
        <StartMenu
          open={startOpen}
          onClose={() => setStartOpen(false)}
          buttonRef={startBtnRef}
          onItemClick={handleItemClick}
        />
        <button
          ref={startBtnRef}
          data-testid={testIds.taskbar.startButton}
          onMouseDown={(e) => {
            e.stopPropagation();
            setStartOpen((v) => !v);
          }}
          className="flex items-center text-white font-bold cursor-pointer select-none"
          style={{
            height: BTN_H,
            padding: "0 22px 0 16px",
            background: startBg,
            border: "1px solid #333333",
            borderRadius: "0 12px 12px 0",
            fontFamily: "var(--xp-font-display)",
            fontSize: 17,
            fontStyle: "italic",
            textShadow: "1px 1px 1px rgba(0,0,0,0.5)",
            boxShadow:
              startOpen
                ? "inset 0 1px 6px rgba(0,0,0,0.4)"
                : "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(0,0,0,0.25)",
            boxSizing: "border-box",
          }}
        >
          Browse
        </button>
      </div>

      <div
        className="flex flex-1 overflow-hidden flex-nowrap"
        style={{ gap: 3 }}
      >
        {visibleWindows.map((win) => {
          const isTop = topWindow?.path === win.path;
          const isMin = win.state === "minimized";
          const active = isTop && !isMin;
          return (
            <button
              key={win.path}
              data-testid={testIds.taskbar.windowButton(win.path)}
              onClick={() => {
                if (isMin) {
                  openWindow(win.path);
                } else {
                  focusWindow(win.path);
                }
              }}
              className="flex items-center gap-1.5 text-white font-bold cursor-pointer select-none"
              aria-pressed={active}
              style={{
                height: BTN_H,
                padding: "0 10px",
                // Monochrome dark gradient. `active` is the pressed-in state.
                background: active
                  ? "linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)"
                  : "linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%)",
                border: "1px solid rgba(0,0,0,0.55)",
                borderRadius: 3,
                fontFamily: "var(--xp-font-body)",
                fontSize: 11,
                color: "#fff",
                textShadow: "1px 1px 1px rgba(0,0,0,0.65)",
                maxWidth: 200,
                minWidth: 120,
                overflow: "hidden",
                whiteSpace: "nowrap",
                flexShrink: 1,
                boxSizing: "border-box",
                opacity: isMin ? 0.75 : 1,
                boxShadow: active
                  ? "inset 0 1px 3px rgba(0,0,0,0.55)"
                  : "inset 0 1px 0 rgba(255,255,255,0.28)",
              }}
            >
              <img
                src={getXpIcon(win.path) ?? XP_ICON_FALLBACK}
                alt=""
                aria-hidden="true"
                draggable={false}
                style={{
                  width: 16,
                  height: 16,
                  imageRendering: "pixelated",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {win.title}
              </span>
            </button>
          );
        })}
      </div>

      {!isMobile && (
        <div
          className="flex items-center text-white"
          style={{
            flexShrink: 0,
            padding: "0 10px",
            height: BTN_H,
            background: "linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%)",
            borderLeft: "1px solid rgba(0,0,0,0.4)",
            borderRight: "1px solid rgba(255,255,255,0.25)",
            fontFamily: "var(--xp-font-body)",
            fontSize: 11,
            userSelect: "none",
            boxSizing: "border-box",
            whiteSpace: "nowrap",
            textShadow: "1px 1px 1px rgba(0,0,0,0.4)",
          }}
        >
          Repancy
        </div>
      )}

      <NipeTrayIcon />
      <NipePendingWritesTray />

      <div
        data-testid={testIds.taskbar.clock}
        className="flex items-center text-white"
        style={{
          flexShrink: 0,
          padding: "0 14px",
          height: BTN_H,
          background: "linear-gradient(180deg, var(--xp-taskbar-from) 0%, var(--xp-taskbar-to) 100%)",
          borderLeft: "1px solid rgba(255,255,255,0.25)",
          fontFamily: "var(--xp-font-body)",
          fontSize: 13,
          userSelect: "none",
          boxSizing: "border-box",
          textShadow: "1px 1px 1px rgba(0,0,0,0.4)",
        }}
      >
        {time ? time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : " "}
      </div>
    </div>
  );
}
